import { ft } from '../core/units.js';

/**
 * What is actually standing in the ballroom.
 *
 * Placement is snapped to the room's anchor grid and nowhere else. That is the
 * central decision of the whole BEO loop, and it is not a shortcut around
 * physics — it is what a banquet setup *is*. Nobody eyeballs a round into
 * position; the room has a diagram and the diagram has a grid, and the skill
 * being tested is reading the diagram and working fast, not nudging a table a
 * foot to the left with a thumbstick. Free placement would also make "correct
 * spacing" a tolerance the player has to guess at, which is a worse game and a
 * much worse thing to grade.
 *
 * An anchor holds a *base*, a *cover* and some *seats*. Those are roles, not
 * object kinds: this module has never heard of a round or a chair, it is told
 * which role an object fills by the object's own data. Seats re-space
 * themselves around the base every time the count changes, because eight chairs
 * evenly spread is what a set table looks like and eight chairs in the first
 * eight of twelve fixed slots is what a bug looks like.
 *
 * Pure. Holds no meshes; `src/world/setupView.js` draws whatever this says.
 */

/** A round physically takes twelve chairs before they start overlapping. */
export const MAX_CHAIRS = 12;

export const key = (col, row) => `${col},${row}`;

export function createSetup({ grid }) {
  const anchors = [];
  const [kx0, kz0, kx1, kz1] = grid.keepClear ?? [0, 0, -1, -1];

  for (let row = 0; row < grid.rows; row += 1) {
    for (let col = 0; col < grid.cols; col += 1) {
      const xFeet = grid.origin[0] + col * grid.spacing;
      const zFeet = grid.origin[1] + row * grid.spacing;
      anchors.push({
        col,
        row,
        key: key(col, row),
        xFeet,
        zFeet,
        x: ft(xFeet),
        z: ft(zFeet),
        /** On the dance floor: legal to place on, always wrong to place on. */
        keepClear: xFeet >= kx0 && xFeet <= kx1 && zFeet >= kz0 && zFeet <= kz1,
      });
    }
  }

  const byKey = new Map(anchors.map((anchor) => [anchor.key, anchor]));
  let tables = new Map();
  const listeners = new Set();

  function emit() {
    for (const listener of listeners) listener();
  }

  /** Where the chairs go for a table with this many of them. */
  function seatsFor(anchor, count, radius) {
    const seats = [];
    for (let i = 0; i < count; i += 1) {
      const angle = -Math.PI / 2 + (i * Math.PI * 2) / count;
      seats.push({
        x: anchor.x + Math.cos(angle) * radius,
        z: anchor.z + Math.sin(angle) * radius,
        angle,
      });
    }
    return seats;
  }

  return {
    anchors,
    grid,
    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    get tables() {
      return [...tables.values()];
    },
    tableAt: (k) => tables.get(k) ?? null,
    anchorAt: (k) => byKey.get(k) ?? null,
    seatsFor,

    get counts() {
      let chairs = 0;
      let linens = 0;
      for (const table of tables.values()) {
        chairs += table.seats;
        if (table.cover) linens += 1;
      }
      return { tables: tables.size, chairs, linens };
    },

    /** The nearest anchor to a point, optionally only empty or only set ones. */
    nearest(position, { within = Infinity, occupied = null } = {}) {
      let best = null;
      for (const anchor of anchors) {
        if (occupied === true && !tables.has(anchor.key)) continue;
        if (occupied === false && tables.has(anchor.key)) continue;
        const distance = Math.hypot(position.x - anchor.x, position.z - anchor.z);
        if (distance > within) continue;
        if (!best || distance < best.distance) best = { anchor, distance };
      }
      return best;
    },

    /**
     * Puts something into a role on an anchor.
     *
     * One entry point for all three roles, because the caller is placement code
     * that read the role off the object and has no business knowing what the
     * difference is between a base and a cover.
     */
    place(anchorKey, { role, kind, variant = null, max = MAX_CHAIRS }) {
      const anchor = byKey.get(anchorKey);
      if (!anchor) return { ok: false, reason: 'no-anchor' };

      if (role === 'base') {
        if (tables.has(anchorKey)) return { ok: false, reason: 'occupied' };
        tables.set(anchorKey, {
          key: anchorKey,
          col: anchor.col,
          row: anchor.row,
          kind,
          variant,
          cover: null,
          seats: 0,
          seatKind: null,
        });
        emit();
        return { ok: true, anchor, role };
      }

      const table = tables.get(anchorKey);
      if (!table) return { ok: false, reason: 'no-base' };

      if (role === 'cover') {
        if (table.cover) return { ok: false, reason: 'already-covered' };
        table.cover = { kind, variant };
        emit();
        return { ok: true, role, variant };
      }

      if (role === 'seat') {
        if (table.seats >= Math.min(max, MAX_CHAIRS)) return { ok: false, reason: 'full' };
        table.seats += 1;
        table.seatKind = kind;
        emit();
        return { ok: true, role, seats: table.seats };
      }

      return { ok: false, reason: 'no-such-role' };
    },

    /**
     * Strikes the top thing at an anchor: seats, then the cover, then the base.
     *
     * That is the order a room is actually struck in, and it means an undo
     * never destroys work you did not ask it to — pulling a misplaced round
     * cannot silently bin the eight chairs on it.
     */
    /** `first` is `'cover'` to take the cloth off before the chairs. */
    strike(anchorKey, first = null) {
      const table = tables.get(anchorKey);
      if (!table) return { ok: false, reason: 'no-base' };
      if (table.seats > 0 && !(first === 'cover' && table.cover)) {
        table.seats -= 1;
        const kind = table.seatKind;
        if (!table.seats) table.seatKind = null;
        emit();
        return { ok: true, role: 'seat', removed: kind };
      }
      if (table.cover) {
        const { kind, variant } = table.cover;
        table.cover = null;
        emit();
        return { ok: true, role: 'cover', removed: kind, variant };
      }
      const kind = table.kind;
      tables.delete(anchorKey);
      emit();
      return { ok: true, role: 'base', removed: kind };
    },

    clear() {
      tables = new Map();
      emit();
    },

    toJSON: () => ({ tables: [...tables.values()] }),
    load(state) {
      tables = new Map((state?.tables ?? []).map((table) => [table.key, { ...table }]));
      emit();
    },
  };
}
